import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getService, SERVICES } from "@/data/services";
import useSeo from "@/lib/useSeo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  useSeo({
    title: service ? `${service.title} — ENVIROTEK LLC FZ` : "Service — ENVIROTEK LLC FZ",
    description: service?.short,
    path: `/services/${slug}`,
  });

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="bg-background min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36" data-testid="service-detail-page">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <Link to="/#services" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary mb-10" data-testid="service-back">
            <ArrowLeft size={14} /> All services
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">/ Service {service.n} · {service.tag}</span>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.88] mt-4"
              >
                {service.title}
              </motion.h1>
            </div>
            <p className="lg:col-span-4 font-sans text-muted-foreground leading-relaxed">{service.intro}</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 pb-24">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 h-[45vh] lg:h-[60vh] overflow-hidden border border-border relative"
            >
              <img src={service.image} alt={service.title} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute bottom-0 left-0 bg-foreground text-background px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
                {service.tag}
              </div>
            </motion.div>

            <div className="lg:col-span-5">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">What's included</p>
              <ul className="border-t border-border">
                {service.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 py-4 border-b border-border">
                    <Check size={18} className="text-primary mt-0.5 shrink-0" />
                    <span className="font-sans text-sm">{p}</span>
                  </li>
                ))}
              </ul>
              <a href="/#quote" data-testid="service-cta" className="mt-8 inline-flex items-center gap-2 bg-primary text-white px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-foreground transition-colors">
                Request this service <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          <div className="border-t border-border py-16">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-8">Explore more</p>
            <div className="grid sm:grid-cols-3 border-t border-l border-border">
              {others.map((o) => (
                <Link key={o.slug} to={`/services/${o.slug}`} className="group border-r border-b border-border p-8 hover:bg-foreground hover:text-background transition-colors">
                  <span className="font-mono text-xs text-muted-foreground group-hover:text-background/50">{o.n}</span>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-4">{o.title}</h3>
                  <ArrowUpRight size={18} className="mt-4 text-primary" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
